import { createInboxMessageHttpRepository } from '@src/main/modules/messages/infrastructure/repository/InboxMessageHttpRepository';
import InvalidDateRangeDialog from '@src/main/shared/ui/components/calendar/InvalidDateRangeDialog';

import { emptyInboxMessageCriteria } from '@src/main/modules/messages/domain/message/inbox/inbox-message-criteria.interface';
import { InboxMessage } from '@src/main/modules/messages/domain/message/inbox/inbox-message.interface';
import MessageDetailModal from '@src/main/modules/messages/ui/mailbox/components/detail/MessageDetailModal';
import {
  getAttachmentColumnProps,
  getMessageColumnProps,
  getRecipientsColumnProps,
  getReplyColumnProps,
  getSendByColumnProps,
  getSendDateColumnProps,
  getSubjectColumnProps,
} from '@src/main/modules/messages/ui/mailbox/components/mailbox/table/MailboxColumns';
import MailboxTable from '@src/main/modules/messages/ui/mailbox/components/mailbox/table/MailboxTable';
import { useFormattedInboxMessages } from '@src/main/modules/messages/ui/mailbox/hooks/mailbox/useFormattedInboxMessages';
import { useMailbox } from '@src/main/modules/messages/ui/mailbox/hooks/mailbox/useMailbox';
import { useMailboxTableHeader } from '@src/main/modules/messages/ui/mailbox/hooks/mailbox/useMailboxHeader';
import { useInboxFilters } from '@src/main/modules/messages/ui/mailbox/hooks/mailbox/useMessageFilters';
import { useReadMessage } from '@src/main/modules/messages/ui/mailbox/hooks/mailbox/useReadMessage';
import { FormattedInboxMessage } from '@src/main/modules/messages/ui/mailbox/model/formated-message.model';
import { CustomFilterMeta } from '@src/main/modules/messages/ui/mailbox/views/mailbox/mailbox.types';
import { createExportHandlers } from '@src/main/modules/messages/utils/createExportHandlers';
import { normalizeInboxMessage } from '@src/main/modules/messages/utils/formatters';
import { handleDateRangeFilter } from '@src/main/modules/messages/utils/handleDateRangeFilter';
import { mapFiltersToCriteria } from '@src/main/modules/messages/utils/mapFiltersToCriteria';
import {
  DateRangeFilter,
  makeKeyAttachmentTemplate,
} from '@src/main/modules/messages/utils/templates';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useDownloadFile } from '@src/main/shared/ui/hooks/useDownloadFile';
import { getThreeMonthDateRange } from '@src/main/shared/utils/dateUtils';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { DataTableFilterMeta } from 'primereact/datatable';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './Inbox.module.scss';

const Inbox: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedMessage, setSelectedMessage] = useState<FormattedInboxMessage | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [invalidDateRangeModalVisible, setInvalidDateRangeModalVisible] = useState(false);
  const [, setSelectedRange] = useState<(Date | null)[] | null>(null);
  const { startDate, endDate } = useMemo(() => getThreeMonthDateRange(), []);

  const navigate = useNavigate();
  const initialFilters = useInboxFilters(startDate, endDate);

  const filtersToCriteria = useCallback(
    (filters: CustomFilterMeta) => mapFiltersToCriteria(filters, emptyInboxMessageCriteria),
    [],
  );

  const {
    filters,
    setFilters,
    messages,
    total,
    loadingRepository,
    pageNumber,
    pageSize,
    handlePageChange,
    sortField,
    sortOrder,
    handleSort,
  } = useMailbox<FormattedInboxMessage>(
    user,
    createInboxMessageHttpRepository,
    useFormattedInboxMessages,
    filtersToCriteria,
    initialFilters,
  );

  const FILE_NAME = t('messages.mailbox.inbox.fileName');
  const { onExportExcel, onExportPdf } = createExportHandlers(
    messages,
    normalizeInboxMessage,
    FILE_NAME,
    t,
  );

  const queryClient = useQueryClient();
  const { mutate: readMessage } = useReadMessage();
  const { mutate: downloadFile } = useDownloadFile();

  const header = useMailboxTableHeader({
    initialFilters,
    setFilters,
    onExportExcel,
    onExportPdf,
  });

  const download = useCallback(
    (message: InboxMessage) => {
      downloadFile({
        keyFile: message.keyAttachment!,
        fileName: message.attachment!,
      });
    },
    [downloadFile],
  );

  const handleSetFilters = (newFilters: DataTableFilterMeta) => {
    const updatedFilters = newFilters as CustomFilterMeta;
    setFilters(updatedFilters);
  };

  const handleRowSelect = (e: { data: FormattedInboxMessage }) => {
    const message = e.data;
    if (!message.readDate) {
      readMessage(message.messageId, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['inboxMessages'] });
        },
      });
    }
    setSelectedMessage(e.data);
    setDetailModalVisible(true);
  };

  const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
    <DateRangeFilter
      value={options.value as (Date | null)[] | null}
      onChange={(val) => {
        setSelectedRange(val);
        options.filterCallback(val, options.index);
      }}
      t={t}
    />
  );

  const recipientsBodyTemplate = useCallback(
    (rowData: InboxMessage) => rowData.recipients.join(', '),
    [],
  );

  const attachmentBodyTemplate = useCallback(
    (rowData: InboxMessage) => makeKeyAttachmentTemplate<InboxMessage>(t, download)(rowData),
    [t, download],
  );

  const handleReply = useCallback(
    (messageId: string) => {
      if (selectedMessage && !selectedMessage.readDate) {
        readMessage(messageId, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inboxMessages'] });
          },
        });
      }
      navigate(`/messages/new-message?messageId=${messageId}`);
    },
    [navigate, queryClient, readMessage, selectedMessage],
  );

  const replyBodyTemplate = useCallback(
    (rowData: InboxMessage) =>
      rowData.isResponded ? null : (
        <Button
          tooltip={t('messages.mailbox.reply')}
          icon="pi pi-reply"
          className={`p-button-text ${styles['reply-button']}`}
          onClick={() => handleReply(rowData.messageId)}
        />
      ),
    [t, handleReply],
  );

  const closeDetailModal = () => {
    setDetailModalVisible(false);
  };

  if (loadingRepository) return <div>Loading...</div>;

  return (
    <>
      <MailboxTable
        messages={messages}
        filters={filters}
        setFilters={handleSetFilters}
        onFilter={(e) =>
          handleDateRangeFilter(e, setFilters, () => setInvalidDateRangeModalVisible(true))
        }
        header={header}
        handleRowSelect={handleRowSelect}
        dataKey="messageId"
        totalRecords={total}
        first={pageNumber * pageSize}
        rows={pageSize}
        onPage={handlePageChange}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={handleSort}
      >
        <Column {...getSendDateColumnProps(t, dateFilterTemplate)} />
        <Column {...getSubjectColumnProps<FormattedInboxMessage>(t)} />
        <Column {...getSendByColumnProps<FormattedInboxMessage>(t)} />
        <Column {...getRecipientsColumnProps(t, recipientsBodyTemplate)} />
        <Column {...getMessageColumnProps(t)} />
        <Column {...getAttachmentColumnProps(t, attachmentBodyTemplate)} />
        <Column {...getReplyColumnProps(t, replyBodyTemplate)} />
      </MailboxTable>

      <MessageDetailModal
        visible={detailModalVisible}
        message={selectedMessage}
        onHide={closeDetailModal}
      />

      <InvalidDateRangeDialog
        invalidDateRangeModalVisible={invalidDateRangeModalVisible}
        setInvalidDateRangeModalVisible={setInvalidDateRangeModalVisible}
        t={t}
      />
    </>
  );
};

export default Inbox;
